'use client'

import { useQuery } from '@apollo/client'
import { Button, Input, Loader2, Search, X } from '@chain-zen/ui'
import Image from 'next/image'
import { useMemo, useState } from 'react'

import type { TAppData } from '../../../../../../../components/apps/apps-data'
import { apps } from '../../../../../../../components/apps/apps-data'
import { DisableAppModal } from '../../../../../../../components/apps/modals/disable-app-modal'
import { GET_APPS } from '../../../../../../../data/queries/apps'
import { useAuth } from '../../../../../../../hooks/use-auth'
import type { TWorkspaceApp } from '../../../../../../../types/apps'
import type { UserRoleEnum } from '../../../../../../../types/user'
import type { AppTypeEnum } from '../../../../../../../types/workspace'
import { AppCategoriesEnum } from '../../../../../../../types/workspace'

interface IEnableELDAppsModalProps {
  isOpen: boolean
  onAppsChanged?: () => void
  onClose: () => void
}

/**
 * Finds an enabled workspace app by app type
 */
const findEnabledWorkspaceApp = (
  workspaceApps: TWorkspaceApp[] | undefined,
  appType: AppTypeEnum
): TWorkspaceApp | undefined => {
  return workspaceApps?.find((wa) => wa.appType === appType && wa.enabled)
}

/**
 * Finds a workspace app by app type
 */
const findWorkspaceApp = (
  workspaceApps: TWorkspaceApp[] | undefined,
  appType: AppTypeEnum
): TWorkspaceApp | undefined => {
  return workspaceApps?.find((wa) => wa.appType === appType)
}

/**
 * Transforms the apps array with workspace-specific data for ELD apps only
 */
const transformELDAppsData = (workspaceApps: TWorkspaceApp[] | undefined): TAppData[] => {
  return apps
    .filter((app) => app.categories.includes(AppCategoriesEnum.ELD) && app.available)
    .map((app) => {
      const workspaceApp = findWorkspaceApp(workspaceApps, app.appType)
      const enabledApp = findEnabledWorkspaceApp(workspaceApps, app.appType)
      return {
        ...app,
        isEnabled: !!enabledApp,
        workspaceApp,
        workspaceID: enabledApp?.workspaceID
      }
    })
}

interface IELDAppCardProps {
  app: TAppData
  isAdminOrOwner: boolean
  onAppChanged: () => void
}

/**
 * Individual ELD app card for the enable modal
 */
const ELDAppCard = ({ app, isAdminOrOwner, onAppChanged }: IELDAppCardProps) => {
  const [isEnableModalOpen, setIsEnableModalOpen] = useState(false)
  const [isDisableModalOpen, setIsDisableModalOpen] = useState(false)

  const handleEnableClick = () => {
    if (!app.isEnabled) {
      setIsEnableModalOpen(true)
    }
  }

  const handleDisableClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsDisableModalOpen(true)
  }

  const handleEnableModalClose = () => {
    setIsEnableModalOpen(false)
    onAppChanged()
  }

  const handleDisableModalClose = () => {
    setIsDisableModalOpen(false)
    onAppChanged()
  }

  return (
    <>
      <div
        className={`p-3 rounded-lg border transition-all ${
          app.isEnabled
            ? 'border-emerald-500/50 bg-emerald-500/10'
            : 'border-border hover:border-zinc-300 dark:hover:border-zinc-600 bg-muted/50 cursor-pointer'
        }`}
        onClick={!app.isEnabled ? handleEnableClick : undefined}
        role={!app.isEnabled ? 'button' : undefined}
        tabIndex={!app.isEnabled ? 0 : undefined}
        onKeyDown={
          !app.isEnabled
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') handleEnableClick()
              }
            : undefined
        }
      >
        <div className="flex items-center gap-3">
          {/* App Icon */}
          <div className="w-10 h-10 rounded-md bg-card border border-border flex items-center justify-center shrink-0 overflow-hidden">
            {app.darkImageURL || app.lightImageURL ? (
              <Image
                src={app.darkImageURL || app.lightImageURL}
                alt={app.appName}
                width={32}
                height={32}
                className="object-contain"
              />
            ) : (
              <span className="text-lg">📊</span>
            )}
          </div>

          {/* App Info */}
          <div className="flex-1 min-w-0">
            <div className="text-foreground text-sm font-medium">{app.appName}</div>
            <div className="text-xs text-muted-foreground truncate">{app.description}</div>
          </div>

          {/* Status / Actions */}
          {app.isEnabled ? (
            isAdminOrOwner ? (
              <Button size="sm" variant="destructive" className="shrink-0" onClick={handleDisableClick}>
                Disable
              </Button>
            ) : (
              <span className="px-2 py-1 text-xs font-medium bg-emerald-500/20 text-emerald-400 rounded">Enabled</span>
            )
          ) : (
            <Button size="sm" variant="outline" className="shrink-0" onClick={handleEnableClick}>
              Enable
            </Button>
          )}
        </div>
      </div>

      {/* App-specific enable modal */}
      <app.modal
        isOpen={isEnableModalOpen}
        onClose={handleEnableModalClose}
        app={app}
        appType={app.appType}
        modalTitle={app.modalTitle}
        isModalEditable={false}
        setModalEditable={() => {}}
        modalSubtitle={app.modalSubtitle}
        oAuthBaseURL={app.oAuthBaseURL}
        oAuthRedirectURISuffix={app.oAuthRedirectURISuffix}
        oAuthScope={app.oAuthScope}
        workspaceApp={app.workspaceApp}
        bookingWorkspaceApp={undefined}
        trackingWorkspaceApp={undefined}
      />

      {/* Disable app modal */}
      {!!isDisableModalOpen && (
        <DisableAppModal isOpen={isDisableModalOpen} onClose={handleDisableModalClose} app={app} />
      )}
    </>
  )
}

/**
 * Modal for enabling ELD apps directly from the truck/trailer selection popup.
 * Shows a list of available ELD apps with enable buttons.
 */
export const EnableELDAppsModal = ({ isOpen, onAppsChanged, onClose }: IEnableELDAppsModalProps) => {
  const [searchValue, setSearchValue] = useState('')
  const [hasChanges, setHasChanges] = useState(false)
  const { isUserAdminOrOwner, selectedWorkspace } = useAuth()
  const isAdminOrOwner = isUserAdminOrOwner((selectedWorkspace?.accessType as UserRoleEnum) ?? '')

  const { data, loading, refetch } = useQuery<{ workspaceApps: TWorkspaceApp[] }>(GET_APPS, {
    skip: !isOpen,
    fetchPolicy: 'network-only'
  })

  const eldApps = useMemo(() => {
    return transformELDAppsData(data?.workspaceApps)
  }, [data?.workspaceApps])

  const filteredApps = useMemo(() => {
    const trimmed = searchValue.trim().toLowerCase()
    if (!trimmed) return eldApps
    return eldApps.filter(
      (app) =>
        app.appName.toLowerCase().includes(trimmed) ||
        app.description.toLowerCase().includes(trimmed) ||
        app.searchMetadata?.some((meta) => meta.toLowerCase().includes(trimmed))
    )
  }, [eldApps, searchValue])

  const handleAppChanged = () => {
    void refetch()
    setHasChanges(true)
  }

  const handleClose = () => {
    if (hasChanges) {
      onAppsChanged?.()
    }
    setHasChanges(false)
    setSearchValue('')
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 dark:bg-zinc-950/80 backdrop-blur-sm">
      <div className="bg-background w-full max-w-lg rounded-lg border border-border shadow-lg overflow-hidden max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-3.5 border-b border-border flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-foreground text-base font-semibold">Enable ELD Apps</h2>
            <p className="text-muted-foreground text-xs mt-0.5">Connect your ELD provider to enable live tracking</p>
          </div>
          <Button
            onClick={handleClose}
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-foreground hover:bg-accent"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Search */}
        <div className="px-5 py-3 border-b border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search ELD apps..."
              value={searchValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
              className="w-full pl-9"
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-5 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-10 gap-2.5">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Loading ELD apps...</p>
            </div>
          ) : filteredApps.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 gap-2.5">
              <p className="text-sm text-muted-foreground">No ELD apps found matching your search.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {filteredApps.map((app) => (
                <ELDAppCard
                  key={app.appType}
                  app={app}
                  isAdminOrOwner={isAdminOrOwner}
                  onAppChanged={handleAppChanged}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border shrink-0">
          <Button onClick={handleClose} variant="ghost" className="w-full">
            Close
          </Button>
        </div>
      </div>
    </div>
  )
}
